import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { Form } from "antd";
import { useSelector, useDispatch } from "react-redux";
import { setChangedFormItems } from "../employeeDetailsSlice";
import { getChangedItems } from "../../../components/util/helpers";
import { useIntl } from "react-intl";
import FloatingFormActions from "../../../components/forms/FloatingFormActions";
import Spacer from "../../../components/layouts/Spacer";
import RestrictedContext from "../../../components/util/RestrictedContext";
import { usePermission } from "../../../hooks/accessControl";
import { useParams } from "react-router-dom";
import { useCourses } from "../../../hooks";
var flatten = require("flat");

const FormWrapper = (props) => {
  const {
    mode,
    onSubmit,
    onFinishFailed,
    onDecline,
    saveLoading,
    children,
    waitingForExport,
  } = props;
  const [form] = Form.useForm();
  const activeEmployee = useSelector(
    (state) => state.employeeDetails.activeEmployee
  );
  const changedItems = useSelector(
    (state) => state.employeeDetails.changedItems.changed
  );
  const changedValues = useSelector(
    (state) => state.employeeDetails.changedItems.values
  );
  const intl = useIntl();
  const courses = useCourses();

  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(setChangedFormItems({ changed: [], values: {} }));
  }, [dispatch]);

  useEffect(() => {
    const flat = flatten(activeEmployee, {
      safe: true,
    });

    form.setFieldsValue({ ...flat, geo: activeEmployee.geo });
    const activePeriod =
      activeEmployee &&
      activeEmployee.work_periods &&
      activeEmployee.work_periods.slice(-1)[0];
    if (activePeriod) {
      form.setFieldsValue({
        startEmployment: activePeriod.start,
        endEmployment: activePeriod.end,
      });
    }
  }, [activeEmployee, form, mode]);

  useEffect(() => {
    if (waitingForExport && activeEmployee.exported_at !== null) {
      form.setFieldsValue({ ...changedValues, exported_at: null });
      dispatch(
        setChangedFormItems({
          changed: [
            ...changedItems.filter((i) => i !== "exported_at"),
            "exported_at",
          ],
          values: { ...changedValues, exported_at: null },
        })
      );
    } else if (!waitingForExport && changedValues.exported_at === null) {
      form.setFieldsValue({
        ...changedValues,
        exported_at: activeEmployee.exported_at,
      });
      dispatch(
        setChangedFormItems({
          changed: [...changedItems.filter((i) => i !== "exported_at")],
          values: {
            ...changedValues,
            exported_at: activeEmployee.exported_at,
          },
        })
      );
    }
  }, [dispatch, waitingForExport]);

  const onValuesChange = (changedValues, allValues) => {
    let arr = getChangedItems(
      changedValues,
      allValues,
      changedItems,
      activeEmployee
    );
    let finalValues = allValues;
    let fieldsToSet = {};
    if (
      changedValues.hasOwnProperty("bank_acccount.bank_id") &&
      changedValues["bank_acccount.bank_id"] === undefined
    ) {
      arr = [...arr, "bank_acccount.branch_id"];
      finalValues = {
        ...finalValues,
        "bank_acccount.branch_id": null,
      };
      fieldsToSet["bank_acccount.branch_id"] = null;
    }

    if (
      changedValues.hasOwnProperty("foreign.deposit_acccount.bank_id") &&
      changedValues["foreign.deposit_acccount.bank_id"] === undefined
    ) {
      arr = [...arr, "foreign.deposit_acccount.branch_id"];
      finalValues = {
        ...finalValues,
        "foreign.deposit_acccount.branch_id": null,
      };
      fieldsToSet["foreign.deposit_acccount.branch_id"] = null;
    }

    if (
      (changedValues.hasOwnProperty("monthly_transportation_cost_id") &&
        changedValues["monthly_transportation_cost_id"] === undefined &&
        allValues["transportation_cost_strategy"] === "enforce_monthly") ||
      (changedValues.hasOwnProperty("daily_transportation_cost_id") &&
        changedValues["daily_transportation_cost_id"] === undefined &&
        allValues["transportation_cost_strategy"] === "enforce_daily")
    ) {
      arr = [...arr, "transportation_cost_strategy"];
      finalValues = {
        ...finalValues,
        transportation_cost_strategy: "regular",
      };
      fieldsToSet["transportation_cost_strategy"] = "regular";
    }

    if (changedValues["certified_courses"]) {
      const newCoursesIds = changedValues["certified_courses"]
        .filter((i) => !i.id && i.course_end_time)
        .map((j) => j.course_id);
      const recognized = courses
        .filter((i) => newCoursesIds.includes(i.id))
        .map((j) => j.recognized);
      if (recognized.includes(true)) {
        arr = [...arr, "certified"];
        finalValues = {
          ...finalValues,
          certified: true,
        };
        fieldsToSet["certified"] = true;
      } else {
        arr = [...arr, "certified"];
        finalValues = {
          ...finalValues,
          certified: activeEmployee.certified,
        };
        fieldsToSet["certified"] = activeEmployee.certified;
      }
    }

    dispatch(setChangedFormItems({ changed: arr, values: finalValues }));
    form.setFieldsValue(fieldsToSet);
    if (
      changedValues["care_giver"] ||
      changedValues["care_giver"] === false ||
      changedValues["staff_member"] ||
      changedValues["staff_member"] === false
    ) {
      form.validateFields();
    }
  };

  let { branchId } = useParams();
  const hasPermission = usePermission(branchId, "employees", "editor");
  return (
    <RestrictedContext.Provider value={{ hasWritePermission: hasPermission }}>
      <Form
        layout="vertical"
        onFinish={onSubmit}
        onFinishFailed={onFinishFailed}
        scrollToFirstError
        onValuesChange={onValuesChange}
        form={form}
      >
        {children}
        <Spacer vertical={true} extra={true} />
        <FloatingFormActions
          resource={"employees"}
          action={"editor"}
          onCancel={onDecline}
          mode={mode}
          formName={activeEmployee.first_name}
          onDecline={onDecline}
          hideDelete={true}
          hideDecline={true}
          saveLoading={saveLoading}
          saveText={intl.formatMessage({
            id: "app.actions.saveChanges",
          })}
          show={changedItems.length > 0}
        />
      </Form>
    </RestrictedContext.Provider>
  );
};

FormWrapper.propTypes = {
  mode: PropTypes.string,
  onSubmit: PropTypes.func,
  onFinishFailed: PropTypes.func,
  onDecline: PropTypes.func,
  addImage: PropTypes.func,
  saveLoading: PropTypes.bool,
  disabled: PropTypes.bool,
};
export default FormWrapper;
